import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics 4 Integration Component
// This component handles GA4 tracking, page views, and custom events

interface GoogleAnalyticsProps {
  measurementId?: string;
  debug?: boolean;
}

const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({
  measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID,
  debug = process.env.NODE_ENV === 'development'
}) => {
  const location = useLocation();

  // Initialize Google Analytics
  useEffect(() => {
    if (!measurementId) {
      if (debug) {
        console.warn('Google Analytics: No measurement ID provided');
      }
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    (window as any).gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Configure GA4
    (window as any).gtag('js', new Date());
    (window as any).gtag('config', measurementId, {
      // Enhanced measurement settings
      enhanced_measurement: true,
      page_title: document.title,
      page_location: window.location.href,
      // Privacy settings
      anonymize_ip: true,
      allow_google_signals: true,
      allow_ad_personalization_signals: false,
      // Custom dimensions for education sector
      custom_map: {
        custom_dimension_1: 'page_language',
        custom_dimension_2: 'school_section',
        custom_dimension_3: 'user_type'
      }
    });

    if (debug) {
      console.log('Google Analytics initialized with ID:', measurementId);
    }

    // Cleanup function
    return () => {
      // Remove script if component unmounts
      const existingScript = document.querySelector(`script[src*="${measurementId}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [measurementId, debug]);

  // Track page views on route changes
  useEffect(() => {
    if (!measurementId || !(window as any).gtag) return;

    // Extract language from path
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const language = pathSegments[0] && ['en', 'fr', 'ar'].includes(pathSegments[0]) 
      ? pathSegments[0] 
      : 'en';

    // Determine school section based on path
    const getSchoolSection = (path: string): string => {
      if (path.includes('preschool') || path.includes('early-years')) return 'Early Years';
      if (path.includes('elementary')) return 'Elementary';
      if (path.includes('middle-school')) return 'Middle School';
      if (path.includes('high-school')) return 'High School';
      if (path.includes('academics') || path.includes('curriculum')) return 'Academics';
      if (path.includes('admissions')) return 'Admissions';
      if (path.includes('faculty')) return 'Faculty';
      if (path.includes('alumni')) return 'Alumni';
      return 'General';
    };

    // Track page view with custom dimensions
    (window as any).gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname,
      custom_dimension_1: language,
      custom_dimension_2: getSchoolSection(location.pathname),
      custom_dimension_3: 'visitor' // Can be enhanced with user authentication
    });

    if (debug) {
      console.log('GA4 Page view tracked:', {
        path: location.pathname,
        language,
        section: getSchoolSection(location.pathname)
      });
    }
  }, [location, measurementId, debug]);

  return null; // This component doesn't render anything
};

// Custom hook for Google Analytics events
export const useGoogleAnalytics = () => {
  const trackEvent = (
    eventName: string,
    parameters: {
      event_category?: string;
      event_label?: string;
      value?: number;
      [key: string]: any;
    } = {}
  ) => {
    if ((window as any).gtag) {
      (window as any).gtag('event', eventName, {
        event_category: parameters.event_category || 'engagement',
        event_label: parameters.event_label,
        value: parameters.value,
        ...parameters
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('GA4 Event tracked:', eventName, parameters);
      }
    }
  };

  // Predefined tracking functions for common school website events
  const trackContactFormSubmission = (formType: 'general' | 'admissions' | 'employment' = 'general') => {
    trackEvent('form_submit', {
      event_category: 'contact',
      event_label: formType,
      form_type: formType
    });
  };

  const trackDownload = (fileName: string, fileType: 'pdf' | 'document' | 'image' = 'pdf') => {
    trackEvent('file_download', {
      event_category: 'download',
      event_label: fileName,
      file_name: fileName,
      file_type: fileType
    });
  };

  const trackVideoPlay = (videoTitle: string, videoUrl?: string) => {
    trackEvent('video_play', {
      event_category: 'video',
      event_label: videoTitle,
      video_title: videoTitle,
      video_url: videoUrl
    });
  };

  const trackSocialShare = (platform: string, url: string) => {
    trackEvent('share', {
      event_category: 'social',
      event_label: platform,
      method: platform,
      content_type: 'page',
      item_id: url
    });
  };

  const trackSearchQuery = (searchTerm: string, resultsCount?: number) => {
    trackEvent('search', {
      event_category: 'search',
      event_label: searchTerm,
      search_term: searchTerm,
      results_count: resultsCount
    });
  };

  const trackLanguageChange = (fromLanguage: string, toLanguage: string) => {
    trackEvent('language_change', {
      event_category: 'internationalization',
      event_label: `${fromLanguage}_to_${toLanguage}`,
      from_language: fromLanguage,
      to_language: toLanguage
    });
  };

  const trackAdmissionsInterest = (programLevel: string, action: 'inquiry' | 'brochure_download' | 'tour_request') => {
    trackEvent('admissions_interest', {
      event_category: 'admissions',
      event_label: `${programLevel}_${action}`,
      program_level: programLevel,
      action_type: action
    });
  };

  return {
    trackEvent,
    trackContactFormSubmission,
    trackDownload,
    trackVideoPlay,
    trackSocialShare,
    trackSearchQuery,
    trackLanguageChange,
    trackAdmissionsInterest
  };
};

export default GoogleAnalytics;
